import { useCallback, useMemo } from 'react';

import Box from '@zenbusiness/zui/Box';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import UploadDocuments from './UploadDocuments';
import { DividerStyled } from './UploadDocuments.styles';

import { APP_DOCS_STATUS } from '../constants/status';
import { APP_DOCS_NAME_BY_TYPE, APP_DOCS_TYPES } from '../constants/types';

function DocumentList({ isDragEnter, documentSelected, handleUpload, handleRemoveFileSelected }) {
  const renderTitleByType = useCallback(
    (isIdDocumentWithFrontSide = false, documentType = '', status = '') => {
      switch (documentType) {
        case APP_DOCS_TYPES.ID_DOCUMENT:
          if (isIdDocumentWithFrontSide || APP_DOCS_STATUS.RECEIVED_BACK === status) {
            return `${APP_DOCS_NAME_BY_TYPE[documentType].title} (front)`;
          }
          return `${APP_DOCS_NAME_BY_TYPE[documentType].title} (back)`;
        default:
          return APP_DOCS_NAME_BY_TYPE[documentType]?.title ?? '';
      }
    },
    []
  );

  const documentsList = useMemo(
    () =>
      documentSelected.map((item, index) => {
        const { status, name, documentType, fileSelected, placeholderId } = item;
        const isIdDocument =
          APP_DOCS_TYPES.ID_DOCUMENT === documentType && APP_DOCS_STATUS.REQUIRED === status;

        const isIdDocumentWithBackSide = isIdDocument && fileSelected && fileSelected.isBack;
        const isIdDocumentWithFrontSide = isIdDocument && fileSelected && !fileSelected.isBack;
        const title = renderTitleByType(isIdDocumentWithFrontSide, documentType, status);

        return (
          <Stack
            key={placeholderId}
            width="100%"
          >
            {!isIdDocumentWithBackSide && (
              <Box
                mb="20px"
                mt="12px"
              >
                <Text size="xl">{APP_DOCS_NAME_BY_TYPE[documentType].heading(name)}</Text>
              </Box>
            )}
            <Stack>
              <UploadDocuments
                documentType={documentType}
                fileName={fileSelected?.file?.name}
                handleRemoveFileSelected={() => handleRemoveFileSelected(index)}
                handleUpload={(file) => handleUpload(index, file)}
                isDragEnter={isDragEnter}
                priorityTitle={title}
                status={status}
              />
            </Stack>
            {!isIdDocumentWithFrontSide && index < documentSelected.length - 1 && <DividerStyled />}
          </Stack>
        );
      }),
    [documentSelected, handleRemoveFileSelected, handleUpload, isDragEnter, renderTitleByType]
  );

  return (
    <Stack
      alignItems="center"
      spacing="20px"
    >
      {documentsList}
    </Stack>
  );
}

DocumentList.propTypes = {
  documentSelected: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      documentType: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      placeholderId: PropTypes.string,
      status: PropTypes.string.isRequired,
      fileSelected: PropTypes.shape({
        file: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
        isBack: PropTypes.bool
      })
    })
  ),
  isDragEnter: PropTypes.bool,
  handleUpload: PropTypes.func.isRequired,
  handleRemoveFileSelected: PropTypes.func.isRequired
};

export default DocumentList;
